import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FilePlus2, Plus, X, Sparkles, Lock } from "lucide-react";
import { PreviewCard } from "./ResumeVersions";
import MarkdownPreview from "../components/MarkdownPreview";
import { downloadFile } from "../lib/api";
import { Download } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const empty_exp = { title: "", company: "", location: "", start_date: "", end_date: "", bullets: "" };
const empty_edu = { degree: "", institution: "", year: "", details: "" };

export default function CreateCV() {
  const { user } = useAuth();
  const isPremium = user?.subscription === "premium" || user?.is_admin;
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", location: "", linkedin: "",
    summary: "",
    experience: [{ ...empty_exp }],
    education: [{ ...empty_edu }],
    skills: "",
    certifications: "",
    languages: "",
  });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [cvQuota, setCvQuota] = useState(null); // { count, limit, remaining }
  const [showLimitModal, setShowLimitModal] = useState(false);

  useEffect(() => {
    if (!isPremium) {
      api.get("/quota").then((r) => setCvQuota(r.data?.cv)).catch(() => {});
    }
  }, [isPremium]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name || !form.email) return toast.error("Name and email required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        certifications: form.certifications.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        experience: form.experience.map((e) => ({ ...e, bullets: e.bullets.split("\n").map((s) => s.trim()).filter(Boolean) })),
      };
      const r = await api.post("/cv/create", payload);
      setData(r.data);
      toast.success("CV ready");
      if (!isPremium) {
        api.get("/quota").then((rr) => setCvQuota(rr.data?.cv)).catch(() => {});
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowLimitModal(true);
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const addExp = () => setForm({ ...form, experience: [...form.experience, { ...empty_exp }] });
  const addEdu = () => setForm({ ...form, education: [...form.education, { ...empty_edu }] });

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Create CV</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Build your CV from scratch</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Fill in the form, get a clean ATS-friendly CV, matching cover letter, and a LinkedIn summary.</p>
      </div>

      {/* CV quota banner (free users) */}
      {cvQuota && cvQuota.limit && (
        <div
          data-testid="cv-quota-banner"
          className={`rounded-2xl border-2 p-4 flex items-center justify-between gap-4 fade-up-delay-1 ${
            cvQuota.remaining === 0 ? "border-amber-300 bg-amber-50" : "border-white/10 bg-white"
          }`}
        >
          <div className="flex items-center gap-3">
            <span className={`grid place-items-center w-10 h-10 rounded-2xl ${
              cvQuota.remaining === 0 ? "bg-amber-200 text-amber-700" : "bg-[rgba(128,255,234,0.1)] text-[#80FFEA]"
            }`}>
              {cvQuota.remaining === 0 ? <Lock size={18} /> : <FilePlus2 size={18} />}
            </span>
            <div>
              <div className="text-sm font-bold text-white">
                {cvQuota.count} of {cvQuota.limit} free CV used
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {cvQuota.remaining === 0
                  ? "You&apos;ve used your free CV. Downloads still work — but new CVs are premium."
                  : "Free plan includes 1 CV with a downloadable PDF (with a small Cavilow watermark)."}
              </div>
            </div>
          </div>
          <Link
            to="/pricing"
            data-testid="cv-quota-upgrade"
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-4 py-2 text-xs font-bold btn-press whitespace-nowrap"
          >
            <Sparkles size={12} /> Take the premium plan
          </Link>
        </div>
      )}

      {/* Upgrade modal (blocks 2nd CV attempt) */}
      {showLimitModal && (
        <div
          data-testid="cv-limit-modal"
          className="fixed inset-0 z-[80] grid place-items-center px-4 py-8"
          onClick={() => setShowLimitModal(false)}
        >
          <div className="absolute inset-0 bg-[var(--cavilo-ink)]/60 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-3xl shadow-2xl border border-white/10 max-w-md w-full p-7 fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 mb-4">
              <Sparkles size={26} />
            </span>
            <h3 className="font-display font-black text-2xl text-white tracking-tight">
              Create tailored CV versions for different roles with Cavilow Premium.
            </h3>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed">
              You&apos;ve reached the free plan&apos;s 1-CV limit. Upgrade to unlimited CVs, no watermark on downloads,
              and DOCX/Word exports.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowLimitModal(false)}
                className="flex-1 rounded-xl border-2 border-white/10 text-slate-300 hover:text-white px-4 py-3 text-sm font-bold"
                data-testid="cv-limit-modal-cancel"
              >
                Maybe later
              </button>
              <Link
                to="/pricing"
                data-testid="cv-limit-modal-upgrade"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-4 py-3 text-sm font-bold btn-press"
              >
                <Sparkles size={14} /> Take the premium plan
              </Link>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="glass p-6 fade-up-delay-1 space-y-6">
        <Section title="Basics">
          <div className="grid sm:grid-cols-2 gap-4">
            <Inp label="Full name" value={form.full_name} onChange={(v) => upd("full_name", v)} testid="cv-name" />
            <Inp label="Email" value={form.email} onChange={(v) => upd("email", v)} testid="cv-email" />
            <Inp label="Phone" value={form.phone} onChange={(v) => upd("phone", v)} testid="cv-phone" />
            <Inp label="Location" value={form.location} onChange={(v) => upd("location", v)} testid="cv-loc" />
            <Inp label="LinkedIn" value={form.linkedin} onChange={(v) => upd("linkedin", v)} testid="cv-linkedin" />
          </div>
        </Section>

        <Section title="Summary">
          <Ta value={form.summary} onChange={(v) => upd("summary", v)} testid="cv-summary" placeholder="2-3 sentence professional summary" rows={3} />
        </Section>

        <Section title="Experience" action={<HeaderBtn onClick={addExp} icon={Plus}>Add role</HeaderBtn>}>
          {form.experience.map((e, i) => (
            <div key={i} className="rounded-xl border border-white/10 p-4 mb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Role #{i + 1}</div>
                {form.experience.length > 1 && (
                  <button type="button" onClick={() => setForm({ ...form, experience: form.experience.filter((_, idx) => idx !== i) })} className="text-slate-400 hover:text-[#FF6DAE]">
                    <X size={14} />
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Inp label="Title" value={e.title} onChange={(v) => setExp(form, setForm, i, { title: v })} />
                <Inp label="Company" value={e.company} onChange={(v) => setExp(form, setForm, i, { company: v })} />
                <Inp label="Location" value={e.location} onChange={(v) => setExp(form, setForm, i, { location: v })} />
                <Inp label="Dates" value={`${e.start_date}${e.end_date ? " — " + e.end_date : ""}`} onChange={(v) => {
                  const [s, t] = v.split("—").map((x) => x.trim());
                  setExp(form, setForm, i, { start_date: s || "", end_date: t || "" });
                }} placeholder="2022-01 — Present" />
              </div>
              <div className="mt-3">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Bullets (one per line)</span>
                <textarea
                  value={e.bullets}
                  onChange={(ev) => setExp(form, setForm, i, { bullets: ev.target.value })}
                  rows={4}
                  className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
                />
              </div>
            </div>
          ))}
        </Section>

        <Section title="Education" action={<HeaderBtn onClick={addEdu} icon={Plus}>Add education</HeaderBtn>}>
          {form.education.map((e, i) => (
            <div key={i} className="rounded-xl border border-white/10 p-4 mb-3 grid sm:grid-cols-2 gap-3">
              <Inp label="Degree" value={e.degree} onChange={(v) => setEdu(form, setForm, i, { degree: v })} />
              <Inp label="Institution" value={e.institution} onChange={(v) => setEdu(form, setForm, i, { institution: v })} />
              <Inp label="Year" value={e.year} onChange={(v) => setEdu(form, setForm, i, { year: v })} />
              <Inp label="Details" value={e.details} onChange={(v) => setEdu(form, setForm, i, { details: v })} />
            </div>
          ))}
        </Section>

        <Section title="Skills, certifications & languages">
          <div className="grid sm:grid-cols-3 gap-4">
            <Inp label="Skills (comma-separated)" value={form.skills} onChange={(v) => upd("skills", v)} testid="cv-skills" />
            <Inp label="Certifications" value={form.certifications} onChange={(v) => upd("certifications", v)} testid="cv-certs" />
            <Inp label="Languages" value={form.languages} onChange={(v) => upd("languages", v)} testid="cv-langs" />
          </div>
        </Section>

        <div className="flex justify-end">
          <button
            data-testid="cv-submit"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-[#1E6FE6] hover:bg-[#1257C4] text-white px-6 py-3 font-bold text-sm shadow-lg shadow-indigo-500/20 disabled:opacity-60"
          >
            <FilePlus2 size={16} /> {busy ? "Generating…" : "Generate CV"}
          </button>
        </div>
      </form>

      {data && (
        <div className="grid lg:grid-cols-2 gap-6 fade-up-delay-2">
          <PreviewCard testid="cv-preview" title="CV" md={data.cv_markdown || ""} filename={`cavilo-cv-${form.full_name.split(" ").join("-")}`} />
          <PreviewCard testid="cv-cover-letter" title="Cover Letter" md={data.cover_letter_markdown || ""} filename="cavilo-cover-letter" />
          <div className="glass overflow-hidden flex flex-col lg:col-span-2">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
              <div className="font-display font-bold">LinkedIn Summary</div>
              <div className="flex gap-2">
                <button onClick={() => downloadFile(data.linkedin_summary || "", "cavilo-linkedin-summary", "pdf")} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F172A] text-white px-3 py-1.5 text-xs font-semibold">
                  <Download size={12} /> PDF
                </button>
              </div>
            </div>
            <div className="p-5">
              <MarkdownPreview>{data.linkedin_summary || ""}</MarkdownPreview>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function setExp(form, setForm, i, patch) {
  const next = form.experience.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
  setForm({ ...form, experience: next });
}
function setEdu(form, setForm, i, patch) {
  const next = form.education.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
  setForm({ ...form, education: next });
}

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function HeaderBtn({ onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E6FE6] hover:text-[#1257C4]">
      <Icon size={14} /> {children}
    </button>
  );
}

function Inp({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
      />
    </label>
  );
}

function Ta({ value, onChange, testid, rows = 4, placeholder }) {
  return (
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
    />
  );
}
